// src/oura/components/visibility/cells.tsx
//
// The marks a question carries wherever it appears (the table, the phone
// list, the drawer): an engine's verdict, whether the site answers it, and
// the real Google search behind it.

import { Fragment } from "react";
import { ArrowDownIcon, ArrowUpIcon, CheckIcon } from "lucide-react";
import type { ReactNode } from "react";
import type { AeoCoverageStatus, AeoSearchMatch } from "@service/aeo";
import { TooltipContent, TooltipProvider, TooltipRoot, TooltipTrigger } from "@component/tooltip";
import { RankChip, RankChipEmpty } from "@oura/ui";
import { cn } from "../../lib/cn";
import { perYear } from "../../topSearches";
import { SITE_LABEL, type VisCell } from "../../visibility/model";
import type { CellTrend } from "../../visibility/questionTrend";

/** The en dash an empty cell shows. Never an em dash, never a zero. */
export function Nil({ label }: { label: string }) {
    return (
        <span title={label} className="text-gray-300">
            <span aria-hidden>–</span>
            <span className="sr-only">{label}</span>
        </span>
    );
}

/** The green check: this engine named the business. */
function NamedMark() {
    return (
        <span aria-hidden className="inline-grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full bg-emerald-500 text-white">
            <CheckIcon size={11} strokeWidth={3} />
        </span>
    );
}

/**
 * The words behind a trend arrow, in the app's tooltip. The trigger is a plain
 * span, not a focus stop: on the phone list the cell sits inside the row's own
 * button, and the same words are in the cell's screen-reader text.
 */
function TrendTip({ trend, children }: { trend: CellTrend; children: ReactNode }) {
    return (
        <TooltipProvider>
            <TooltipRoot delayDuration={150}>
                <TooltipTrigger asChild>{children}</TooltipTrigger>
                <TooltipContent side="top" className="max-w-[min(18rem,calc(100vw-2rem))] space-y-0.5 text-left">
                    <p className="m-0 font-medium">{trend.headline}</p>
                    <p className="m-0">{trend.detail}</p>
                    <p className="m-0 opacity-70">{trend.caveat}</p>
                </TooltipContent>
            </TooltipRoot>
        </TooltipProvider>
    );
}

/** Up or down beside the engine's mark: it moved since the previous scan. */
function TrendArrow({ trend }: { trend: CellTrend }) {
    const up = trend.dir === "up";
    const Icon = up ? ArrowUpIcon : ArrowDownIcon;
    return (
        <span aria-hidden className={cn("inline-flex shrink-0", up ? "text-emerald-600" : "text-rose-600")}>
            <Icon size={13} strokeWidth={2.5} />
        </span>
    );
}

/**
 * Not measured: a hollow dashed ring, never the en dash a measured miss
 * shows. The two read differently because they are different facts, and a
 * question an engine never answered must not pass for one it answered without
 * the business.
 */
function UnmeasuredMark() {
    return <span aria-hidden className="inline-block h-3 w-3 shrink-0 rounded-full border border-dashed border-gray-400" />;
}

/**
 * A green check where the engine named the business, with "2/3" only when it
 * was asked more than once. Live runs ask once, so live cells are the check.
 */
export function EngineMark({
    cell,
    engine,
    pending = false,
    trend = null,
}: {
    cell: VisCell | null;
    engine: string;
    /** Google's AI page still being read (ourteam-backend#5619): neither a miss nor a failure. */
    pending?: boolean;
    /** Moved since the previous scan on the same questions (#4125). */
    trend?: CellTrend | null;
}) {
    if (pending) {
        return (
            <span title={`${engine}: still being read`} className="text-[13px] text-gray-500">
                <span aria-hidden>Being read</span>
                <span className="sr-only">{engine}: still being read</span>
            </span>
        );
    }
    if (!cell) {
        return (
            <span title={`${engine}: not measured`} className="inline-flex items-center justify-center">
                <UnmeasuredMark />
                <span className="sr-only">{engine}: not measured</span>
            </span>
        );
    }
    if (!cell.named) {
        if (!trend) return <Nil label={`${engine}: not named`} />;
        return (
            <TrendTip trend={trend}>
                <span className="inline-flex items-center justify-center gap-1">
                    <span aria-hidden className="text-gray-300">–</span>
                    <TrendArrow trend={trend} />
                    <span className="sr-only">{engine}: not named. {trend.title}</span>
                </span>
            </TrendTip>
        );
    }
    const mark = (
        <span className="inline-flex items-center justify-center gap-1.5">
            <NamedMark />
            {trend && <TrendArrow trend={trend} />}
            <span className="sr-only">{engine}: named{trend ? `. ${trend.title}` : ""}</span>
            {cell.samples > 1 && (
                <span className="text-[13px] tabular-nums text-gray-600">
                    {cell.hits}/{cell.samples}
                </span>
            )}
        </span>
    );
    return trend ? <TrendTip trend={trend}>{mark}</TrendTip> : mark;
}

/** The key under a table of engine marks: the three marks, in words. */
export function EngineMarkKey({ unmeasured, trended = false }: { unmeasured: boolean; trended?: boolean }) {
    // Each entry once, a subtle bar between them.
    const items = [
        <span key="named" className="inline-flex items-center gap-1.5"><NamedMark />Named</span>,
        <span key="not" className="inline-flex items-center gap-1.5"><span aria-hidden className="text-gray-300">–</span>Not named</span>,
        unmeasured && <span key="unmeasured" className="inline-flex items-center gap-1.5"><UnmeasuredMark />Not measured</span>,
        trended && (
            <span key="moved" className="inline-flex items-center gap-1.5" title="Moved since the previous scan">
                <span aria-hidden className="inline-flex text-emerald-600"><ArrowUpIcon size={13} strokeWidth={2.5} /></span>
                <span aria-hidden className="inline-flex text-rose-600"><ArrowDownIcon size={13} strokeWidth={2.5} /></span>
                Recent change
            </span>
        ),
    ].filter(Boolean);
    return (
        <span className="inline-flex flex-wrap items-center gap-x-2.5 gap-y-1">
            {items.map((item, i) => (
                <Fragment key={i}>
                    {i > 0 && <span aria-hidden className="text-gray-300">|</span>}
                    {item}
                </Fragment>
            ))}
        </span>
    );
}

const SITE: Record<AeoCoverageStatus, { text: string; dot: string }> = {
    covered: { text: "text-emerald-700", dot: "bg-emerald-500" },
    partial: { text: "text-amber-800", dot: "bg-amber-500" },
    missing: { text: "text-gray-500", dot: "bg-gray-300" },
};

/** Whether the business's own site answers the question: dot and word. */
export function SiteStatus({ status }: { status: AeoCoverageStatus | null }) {
    if (!status) return <Nil label="Your site: not checked on this scan" />;
    const s = SITE[status];
    return (
        <span className={cn("inline-flex items-center gap-1.5 text-[13px] font-medium leading-[18px]", s.text)}>
            <span aria-hidden className={cn("h-2 w-2 shrink-0 rounded-full", s.dot)} />
            {SITE_LABEL[status]}
        </span>
    );
}

/**
 * The strongest real search on the question's slot, with the rest one hover
 * away. No chip means no measured search, never a zero: Google hides
 * low-volume searches.
 */
export function SearchChip({ searches, empty }: { searches: AeoSearchMatch[]; empty?: { label: string; why: string } }) {
    if (searches.length === 0) return empty ? <RankChipEmpty label={empty.label} why={empty.why} /> : null;
    const detail = searches.map((m) => `"${m.query}" · ${perYear(m.searches)} · Google #${Math.round(m.position)}`).join("\n");
    return (
        <span className="inline-flex items-center gap-1" title={detail}>
            <RankChip rank={searches[0].rank} />
            {searches.length > 1 && <span className="text-xs text-gray-400">+{searches.length - 1}</span>}
        </span>
    );
}
